import { useEffect, type ReactNode } from 'react'
import { STATUS_TONE, periodOptions } from '../lib/format'

/**
 * 各式列印表的共用抬頭。三排：logo 靠左，接著置中的前綴與主標題。
 *
 *   [logo]
 *   文案產品委外      ← lead：小字前綴
 *   發包單             ← title：文件類型，放大
 *
 * logo 放在 public/logo.png；檔案不在時直接隱藏，不在正式文件上印出破圖。
 * sub 用來放標題下方的補充資訊（例如匯總表的「申請日期」、請款表的「申請月份」）。
 * subAlign 預設置中；發包申請的日期要靠右對齊，才傳 'right'。
 */
export function DocHead({
  lead,
  title,
  sub,
  subAlign = 'center',
}: {
  lead?: string
  title: string
  sub?: ReactNode
  subAlign?: 'center' | 'right'
}) {
  return (
    <>
      <div className="doc-head">
        <img
          className="logo"
          src="/logo.png"
          alt="崴浤科技股份有限公司"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
        {lead && <p className="lead">{lead}</p>}
        <h1>{title}</h1>
      </div>
      {sub && <p className="en" style={{ textAlign: subAlign }}>{sub}</p>}
    </>
  )
}

export function Field({
  label,
  children,
  span,
}: {
  label: string
  children: ReactNode
  /** 放在 .grid.c12 裡時要佔幾欄；沒給就照網格預設 */
  span?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
}) {
  return (
    <label className={span ? `field s${span}` : 'field'}>
      <span>{label}</span>
      {children}
    </label>
  )
}

export function Panel({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="panel">
      {title && (
        <h3 style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{title}</span>
          {actions}
        </h3>
      )}
      {children}
    </section>
  )
}

export function Tag({ value }: { value: string | null | undefined }) {
  if (!value) return null
  return <span className={`tag ${STATUS_TONE[value] ?? ''}`}>{value}</span>
}

export function Alert({ kind, children }: { kind: 'error' | 'warn' | 'ok'; children: ReactNode }) {
  if (!children) return null
  return <div className={`alert ${kind}`}>{children}</div>
}

export function PeriodPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 130 }}>
      {periodOptions().map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
    </select>
  )
}

export function Empty({ children = '目前沒有資料' }: { children?: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function Loading() {
  return <div className="empty">載入中…</div>
}

/**
 * 置中對話框
 *
 * 用在「按下按鈕之後、進入下一頁之前」需要先問一件事的場合。
 * 點遮罩或按 Esc 都會關閉，跟月份選擇器面板的收合行為一致。
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onClose])

  return (
    <div className="modal-mask" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">{title}</div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}
